import { Component, OnDestroy, OnInit, SecurityContext  } from '@angular/core';
import { Router } from '@angular/router';
import { DataService } from '../../../core/services/data.service';
import { Store } from '../../../store';
import { User } from '../../../core/models/User';
import { Ccr } from '../../../core/models/Ccr';

import * as _ from 'underscore';
import { CellRenderersService } from '../../../core/services/cellRenderers.service';
import { ShockMtmValueComponent } from '../../shared/components/shock-mtm-value/shock-mtm-value.component';
import { YearRangeTail } from '../../../core/models/YearRangeTail';
import { EntityTailConfig } from '../../../core/models/EntityTailConfig';
import { LowestCcrsAndShockedCcrs } from '../../../core/models/LowestCcrsAndShockedCcr';
import { LegalEntityAndCounterPartyWCCR } from '../../../core/models/LegalEntityAndCounterPartyWCCR';
import { DerivativeExposure } from '../../../core/models/DerivativeExposure';
import { GridOptions } from 'ag-grid-community';
import { CsvExportParams } from 'ag-grid-community';
import { ValueGetterParams } from 'ag-grid-community';
import { QueryConfig } from '../../../core/models/QueryConfig';
import { UtilityService } from '../../../core/services/utility.service';
import { Observable, of, Subject, combineLatest } from 'rxjs';
import { ApplicationEventsService } from '../../../core/services/application-events.service';
import { delay, takeUntil, distinctUntilChanged, filter } from 'rxjs/operators';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

function genericFormatter(params: ValueGetterParams): any {
    const fieldName: string = params.colDef.field;
    const formattedFieldName = fieldName + 'Formatted';

    if (!params.data.hasOwnProperty(formattedFieldName)) {
        return params.data[fieldName]; 
    }
    return params.data[formattedFieldName];
}

function dateFormatter(params) {
    if (params.value) {
        const date = new Date(params.value);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    }
    return '';
}

@Component({
    selector: 'app-overview',
    templateUrl: './overview.component.html',
    styleUrls: ['./overview.component.scss'],
    standalone: false
})
export class OverviewComponent implements OnDestroy, OnInit {
    public unsubscribe: Subject<void> = new Subject();

    apiToken: string = undefined;
    ccrList: any[];
    user: User;
    legalEntityIds: any = {};
    entityTailConfigs: EntityTailConfig[];
    shockMtmValue: string;
    lowestCcrList: Ccr[];
    lessThan100List: Ccr[];
    between100And200List: Ccr[];
    between200And300List: Ccr[];
    plus300List: Ccr[];
    tenLowestWccrsWithShock: LowestCcrsAndShockedCcrs[];
    legalEntityAndCounterPartyWCCRs: LegalEntityAndCounterPartyWCCR[];
    WCCRTotal: LegalEntityAndCounterPartyWCCR[];
    derivativeExposures: DerivativeExposure[];
    derivativeExposuresTotal: DerivativeExposure[];

    lowestWccrGridOptions: GridOptions;
    domLayout: 'normal' | 'autoHeight' | 'print' = 'autoHeight';

    legalEntityCounterpartySummaryGridOption: GridOptions;
    legalEntityCounterpartySummaryColumnDef: any[];
    legalEntityCounterpartySummaryRowData: any[];
    pinnedBottomRowData: any[];

     totalGridOptions = <GridOptions>{
        suppressCellSelection: true,
        alignedGrids: [],
        suppressHorizontalScroll: true
    };
    totalColumnDef: any[];

    lowestWccrColumnDef: any[];
    wccrColumnDef: any[];
    derivativeExposureColumnDef: any[];
    allCcrTableSearchText: string;

    maxTopBottomCount = 10;

    isWCCRTableSelected :boolean ;
    showLegalEntity :boolean =true;

    showModelBar = false;

    isModellingOnPage = false;
    currentQueryConfig: QueryConfig = {
        rolloverToCash: true,
        setForGroup: false,
        entityTailConfigs: undefined,
        groupTailConfig: undefined,
        rollOverConfigRowKey: '',
        modelId: ''
    };

    public lowestWccrGridApi;
    public legalEntityCounterpartySummaryGridApi;
    loadingLowestWccr = false;

    overlayLoadingTemplate: string;
    overlayNoRowsTemplate: string;

    tenLowestWccrsWithShockQueryInProgress = false;

    constructor(private dataService: DataService, private router: Router, private store: Store,
        private cellRenderersService: CellRenderersService, private utilityService: UtilityService,
        private appEvents: ApplicationEventsService, private sanitizer: DomSanitizer) {

        this.overlayLoadingTemplate = this.utilityService.overlayLoadingTemplate;
        this.overlayNoRowsTemplate = this.utilityService.overlayNoRowsTemplate;

        this.lowestWccrColumnDef = [
            {
                headerName: 'Legal Entity', field: 'legalEntity',
                cellRenderer: cellRenderersService.legalEntity,
                width: 110
            },
            {headerName: 'Counter Party', field: 'counterParty', width: 130, headerClass: 'counterParty'},
            {
                headerName: 'WCCR',
                children: [
                    {headerName: 'WCCR', field: 'wccr', valueFormatter:  genericFormatter, width: 110},
                    {headerName: 'Occurs', field: 'occur', width: 110},
                    {headerName: 'Cash SF', field: 'cashSF', valueFormatter: genericFormatter, width: 180},
                    {headerName: 'Treasury SF', field: 'treasurySF', valueFormatter: genericFormatter, width: 130}
                ]
            },
            {
                headerName: 'Shocked WCCR',
                headerGroupComponentFramework: ShockMtmValueComponent,
                children: [
                    {headerName: 'CCR', field: 'shockedWccr', valueFormatter: genericFormatter, width: 130},
                    {headerName: 'Cash SF', field: 'shockedCashSF', valueFormatter: genericFormatter, width: 130},
                    {headerName: 'Treasury SF', field: 'shockedTreasurySF', valueFormatter: genericFormatter, width: 130}
                ],
                
            }
        ];

        this.wccrColumnDef = [
            {
                headerName: 'Legal Entity', field: 'legalEntity',
                cellRenderer: cellRenderersService.legalEntity,
                getQuickFilterText: cellRenderersService.legalEntity,
                width: 180
            },
            { headerName: 'Fund', field: 'fund', width: 150 },
            { headerName: 'Counter Party', field: 'counterParty', width: 200 },
            { headerName: 'Swap Not', field: 'swapNotional', valueFormatter: genericFormatter, width: 150 },
            { headerName: 'Col MV', field: 'colMV', valueFormatter: genericFormatter, width: 130 },
            { headerName: 'Repo Not', field: 'repoNotional', valueFormatter: genericFormatter, width: 130 },
            { headerName: 'Swap MTM', field: 'swapMTM', valueFormatter: genericFormatter, width: 180 },
            { headerName: 'WCCR', field: 'wccr', valueFormatter: genericFormatter, width: 150 },
            { headerName: 'IMP COL VAL', field: 'impColVal', valueFormatter: genericFormatter, width: 150 },
            { headerName: 'IMP REPO EXP', field: 'impRepoExp', valueFormatter: genericFormatter, width: 150 },
            { headerName: 'IMP SWAP MTM', field: 'impSwapMtm', valueFormatter: genericFormatter, width: 180 },
            { headerName: 'IMP TIMING', field: 'impTiming', valueFormatter: dateFormatter, width: 150 },
        ];

        this.derivativeExposureColumnDef = [
            {
                headerName: 'Legal Entity', field: 'legalEntity',
                cellRenderer: cellRenderersService.legalEntity,
                getQuickFilterText: cellRenderersService.legalEntity,
                width: 110
            },
            { headerName: 'Fund', field: 'fund', width: 110 },
            { headerName: 'Counter Party', field: 'counterParty', width: 180 },
            { headerName: 'Pay Not', field: 'payNotionalFormatted', width: 180 },
            { headerName: 'Net MTM', field: 'netMTMFormatted', width: 180 },
            { headerName: '2w VaR', field: 'w2VarFormatted', width: 180 },
            { headerName: 'Peak CE', field: 'peakCEFormatted', width: 180 },
            { headerName: 'Threshold', field: 'thresholdFormatted', width: 150 },
            { headerName: 'Tot CE', field: 'totCEFormatted', width: 150 },
            { headerName: 'Exp/Not%', field: 'expNotFormatted', width: 150 },
            { headerName: 'Peak Liq Exp', field: 'peakLiqExpFormatted', width: 150 }
        ];
        this.initialiseGridOption();
    }

    ngOnInit() {
        this.apiToken = this.store.value.apiToken;
        this.shockMtmValue = this.store.value.shockMtmValue;
        this.store.select<User>('user').pipe(distinctUntilChanged((prev, curr) => _.isEqual(prev, curr)))
            .subscribe((user: User) => {
                this.user = user;
                /* Store a mapping (legalEntity.dataName => entity ID). */
                this.user.legalEntities.forEach(e => this.legalEntityIds[e.dataName] = e.entityId);
                this.reloadPageData();
            });

        this.subscribeShockMtmValueChange();
        this.subscribeOnPageReload();
    }

    public loadGridData() {
        this.getLowestCcrList(this.currentQueryConfig.setForGroup ?
            this.currentQueryConfig.groupTailConfig : null, this.currentQueryConfig.entityTailConfigs, this.shockMtmValue,
            this.currentQueryConfig.rolloverToCash);
        this.get10LowestCcrList(this.currentQueryConfig.setForGroup ?
            this.currentQueryConfig.groupTailConfig : null, this.currentQueryConfig.entityTailConfigs, this.shockMtmValue,
            this.currentQueryConfig.rolloverToCash);
        this.getWccrSummary(this.currentQueryConfig.setForGroup ?
            this.currentQueryConfig.groupTailConfig : null, this.currentQueryConfig.entityTailConfigs, this.shockMtmValue,
            this.currentQueryConfig.rolloverToCash);
    }

    public initialiseGridOption() {
        if (!this.legalEntityCounterpartySummaryGridOption) {
            this.legalEntityCounterpartySummaryGridOption = <GridOptions>{};
        }
        this.legalEntityCounterpartySummaryGridOption.suppressRowDeselection = true;
    }

    public showWCCRTable() {
        this.legalEntityCounterpartySummaryColumnDef = this.wccrColumnDef;
        this.legalEntityCounterpartySummaryRowData = this.legalEntityAndCounterPartyWCCRs;
        this.totalColumnDef = this.wccrColumnDef;
        this.pinnedBottomRowData = this.WCCRTotal;
    }

    public showDerivativeExposureTable() {
        this.legalEntityCounterpartySummaryColumnDef = this.derivativeExposureColumnDef;
        this.legalEntityCounterpartySummaryRowData = this.derivativeExposures;
        this.totalColumnDef = this.derivativeExposureColumnDef;
        this.pinnedBottomRowData = this.derivativeExposuresTotal;
    }

    public get10LowestCcrList(groupLevelTail: YearRangeTail[], countryLevelTails: EntityTailConfig[], shockMtmValue: string,
        rolloverToCash: boolean) {
        this.showLowestWccrGridOverlay();
        this.tenLowestWccrsWithShockQueryInProgress = true;
        this.dataService
            .getTenLowestWccrsWithShock(groupLevelTail, countryLevelTails, shockMtmValue, rolloverToCash)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((x: any) => {
                this.tenLowestWccrsWithShockQueryInProgress = false;
                this.hideLowestWccrGridOverlay();
                if (!x.lowestTenCcrsAndShockedCcrsList) {
                    return;
                }
             this.tenLowestWccrsWithShock = x.lowestTenCcrsAndShockedCcrsList;
            }, error => {
                this.tenLowestWccrsWithShockQueryInProgress = false;
                this.hideLowestWccrGridOverlay();
                this.tenLowestWccrsWithShock = [];
                alert('Error Happened while trying to load "10 Lowest Wccr"...');
            });
    }

    public getWccrSummary(groupLevelTail: YearRangeTail[], countryLevelTails: EntityTailConfig[], shockMtmValue: string,
        rolloverToCash: boolean) {
        this.showLegalEntityCounterpartySummaryGridOverlay();
        this.dataService
            .getWccrSummary(groupLevelTail, countryLevelTails, shockMtmValue, rolloverToCash)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((x: any) => {
                this.hideLegalEntityCounterpartySummaryGridOverlay();
                if (!x.legalEntityAndCounterPartySummaryResponse) {
                    return;
                }
                this.legalEntityAndCounterPartyWCCRs = x.legalEntityAndCounterPartySummaryResponse.legalEntityAndCounterPartyWCCRList;
                this.WCCRTotal = [x.legalEntityAndCounterPartySummaryResponse.total];
                this.showWCCRTable();
            }, error => {
                this.hideLegalEntityCounterpartySummaryGridOverlay();
                this.legalEntityAndCounterPartyWCCRs = [];
                this.WCCRTotal = [];
                alert('Error Happened while trying to load Wccr Summary...');
            });
    }

    public getDerivativeExposureSummary(groupLevelTail: YearRangeTail[], countryLevelTails: EntityTailConfig[], shockMtmValue: string,
        groupLevel: boolean, rolloverToCash: boolean) {
        this.showLegalEntityCounterpartySummaryGridOverlay();
        this.dataService
            .getDerivativeExposureSummary(groupLevelTail, countryLevelTails, shockMtmValue, groupLevel, rolloverToCash)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((x: any) => {
                this.hideLegalEntityCounterpartySummaryGridOverlay();
                if (!x) {
                    return;
                }
                this.derivativeExposures = x.derivativeExposureResponse.derivativeExposures;
                this.derivativeExposuresTotal = [x.derivativeExposureResponse.total];
                this.showDerivativeExposureTable();
            }, error => {
                this.hideLegalEntityCounterpartySummaryGridOverlay();
                this.derivativeExposures = [];
                this.derivativeExposuresTotal = [];
                alert('Error happened, while trying to load "Derivative Exposure Summary"...');
            });
    }

    // used for fake data
    /*
    private getDerivativeExposureSummaryHide(groupLevelTail: YearRangeTail[], countryLevelTails: EntityTailConfig[], shockMtmValue: string,
        groupLevel: boolean, rolloverToCash: boolean) {
        this.showLegalEntityCounterpartySummaryGridOverlay();
        this.dataService
            .getDerivativeExposureSummaryHide(groupLevelTail, countryLevelTails, shockMtmValue, groupLevel, rolloverToCash)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((x: any) => {
                this.hideLegalEntityCounterpartySummaryGridOverlay();
                if (!x) {
                    return;
                }

                this.derivativeExposures = x.derivativeExposureResponse.derivativeExposures;
                this.derivativeExposuresTotal = [x.derivativeExposureResponse.total];
                this.showDerivativeExposureTable();
            }, error => {
                this.hideLegalEntityCounterpartySummaryGridOverlay();
                this.derivativeExposures = [];
                this.derivativeExposuresTotal = [];
                alert('Error happened, while trying to load "Derivative Exposure Summary"...');
            });
    }
    */

    public getLowestCcrList(groupLevelTail: YearRangeTail[], countryLevelTails: EntityTailConfig[], shockMtmValue: string,
        rolloverToCash: boolean) {
        this.loadingLowestWccr = true;
        this.dataService
            .getLowestWccrList(groupLevelTail, countryLevelTails, shockMtmValue, rolloverToCash)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((x: any) => {
                this.loadingLowestWccr = false;
                this.ccrList = x.lowestWccrList;
                if (!this.ccrList) {
                    return;
                }
                // We may receive null entity Ids => compute them using the legalEntities of the User 
                this.ccrList.forEach(ccr => ccr.legalEntity.entityId = this.legalEntityIds[ccr.legalEntity.dataName]);
                this.setLowestCcrList(this.ccrList);
                this.setLessThan100List(this.ccrList);
                this.setBetween100And200List(this.ccrList);
                this.setBetween200And300List(this.ccrList);
                this.setPlus300List(this.ccrList);
            }, error => {
                this.loadingLowestWccr = false;
                alert('Error happened, while trying to load "Lowest Ccr List"...');
            });
    }

    public setLessThan100List(ccrList: Ccr[]) {
        this.lessThan100List = _.filter(ccrList, x => x.ccr < 100);
    }

    public setBetween100And200List(ccrList: Ccr[]) {
        this.between100And200List = _.filter(ccrList, x => x.ccr >= 100 && x.ccr <= 200);
    }

    public setBetween200And300List(ccrList: Ccr[]) {
        this.between200And300List = _.filter(ccrList, x => x.ccr >= 200 && x.ccr <= 300);
    }

    public setPlus300List(ccrList: Ccr[]) {
        this.plus300List = _.filter(ccrList, x => x.ccr > 300);
    }

    public setLowestCcrList(ccrList: Ccr[]) {
        if (ccrList.length >= this.maxTopBottomCount) {
            this.lowestCcrList = ccrList.slice(0, this.maxTopBottomCount);
        } else {
            this.lowestCcrList = ccrList;
        }
    }

    public getGridCsvExportParam(): CsvExportParams {
        const param = <CsvExportParams>{};
        const formatter = this.utilityService.csvFormatter;
        param.processCellCallback = function (columnParams) {
            let val: any;
            const column = columnParams.column.getColDef();
            if (column && column.field && column.field === 'legalEntity' && columnParams.value != null) {
                val = columnParams.value.dataName;
            } else {
                val = columnParams.value;
            }
            return formatter(val);
        };
        return param;
    }

    onLowestWccrGridReady(params) {

        this.lowestWccrGridApi = params.api;
        //setGridAutoHeight() not found in the new angular ag-grid version
        //use domLayout instead
        //domLayout: 'normal' | 'autoHeight' | 'print' = 'autoHeight';

        //this.lowestWccrGridApi.setGridAutoHeight(true);

        this.lowestWccrGridApi.resetRowHeights()
        this.lowestWccrGridApi.sizeColumnsToFit();
    }

    onSummaryGridReady(params) {
        this.legalEntityCounterpartySummaryGridApi = params.api;
        this.legalEntityCounterpartySummaryGridApi.sizeColumnsToFit();

        //setGridAutoHeight() not found in the new angular ag-grid version
        //use domLayout instead
        //domLayout: 'normal' | 'autoHeight' | 'print' = 'autoHeight';

        //this.legalEntityCounterpartySummaryGridApi.setGridAutoHeight(true);
    }

    onSummaryExport() {
        this.legalEntityCounterpartySummaryGridApi.exportDataAsCsv(this.getGridCsvExportParam());
    }

    onLowestCcrExport() {
        this.lowestWccrGridApi.exportDataAsCsv(this.getGridCsvExportParam());
    }

    onTableSelection(value) {
        this.isWCCRTableSelected = value;
        if (this.isWCCRTableSelected) {
            this.getWccrSummary(this.currentQueryConfig.setForGroup ?
                this.currentQueryConfig.groupTailConfig : null, this.currentQueryConfig.entityTailConfigs, this.shockMtmValue,
                this.currentQueryConfig.rolloverToCash);
        } else {

            this.getDerivativeExposureSummary(this.currentQueryConfig.setForGroup ?
                this.currentQueryConfig.groupTailConfig : null, this.currentQueryConfig.entityTailConfigs,
                this.shockMtmValue, this.showLegalEntity,
                this.currentQueryConfig.rolloverToCash);
        }
    }

    toggleShowLegalEntity(value) {
        this.showLegalEntity = value;
        this.getDerivativeExposureSummary(this.currentQueryConfig.setForGroup ?
            this.currentQueryConfig.groupTailConfig : null, this.currentQueryConfig.entityTailConfigs,
            this.shockMtmValue, this.showLegalEntity, this.currentQueryConfig.rolloverToCash);

        /*
        //used for fake data

        if(!this.showLegalEntity){
            this.getDerivativeExposureSummaryHide(this.currentQueryConfig.setForGroup ?
            this.currentQueryConfig.groupTailConfig : null, this.currentQueryConfig.entityTailConfigs,
            this.shockMtmValue, this.showLegalEntity, this.currentQueryConfig.rolloverToCash);
        }else{
            this.getDerivativeExposureSummary(this.currentQueryConfig.setForGroup ?
            this.currentQueryConfig.groupTailConfig : null, this.currentQueryConfig.entityTailConfigs,
            this.shockMtmValue, this.showLegalEntity, this.currentQueryConfig.rolloverToCash);
        }
        */



    }

    public setModelMode() {
        this.currentQueryConfig.rolloverToCash = this.store.value.modellingConfig.rolloverToCash;
        this.currentQueryConfig.setForGroup = this.store.value.modellingConfig.setForGroup;
        this.currentQueryConfig.groupTailConfig = this.store.value.modellingConfig.groupTailConfig;
        this.currentQueryConfig.entityTailConfigs = this.store.value.modellingConfig.entityTailConfigs;
    }

    public setNonModelMode() {
        this.currentQueryConfig.rolloverToCash = this.user.queryConfig.rolloverToCash;
        this.currentQueryConfig.setForGroup = this.user.queryConfig.setForGroup;
        this.currentQueryConfig.groupTailConfig = this.user.queryConfig.groupTailConfig;
        this.currentQueryConfig.entityTailConfigs = this.user.queryConfig.entityTailConfigs;
    }

    public subscribeShockMtmValueChange() {
        this.store.select<string>('shockMtmValue')
            .pipe(
                filter(Boolean),
                takeUntil(this.unsubscribe)
            )
            .subscribe((shockMtmValue: string) => {
                this.shockMtmValue = shockMtmValue;
                if (!this.currentQueryConfig.entityTailConfigs || this.tenLowestWccrsWithShockQueryInProgress) {
                    return;
                }
                this.get10LowestCcrList(this.currentQueryConfig.setForGroup ?
                    this.currentQueryConfig.groupTailConfig : null, this.currentQueryConfig.entityTailConfigs, this.shockMtmValue,
                    this.currentQueryConfig.rolloverToCash);

            });
    }

    public subscribeOnPageReload() {
        this.appEvents.pageReload
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(x => {
                this.reloadPageData();
            });
    }

    public reloadPageData() {
        this.isModellingOnPage = (this.store.value.urlModelToggle[this.router.url] &&
            this.store.value.urlModelToggle[this.router.url] === true);

        this.isWCCRTableSelected = true;
        if (!this.isModellingOnPage) {
            this.setNonModelMode();
            this.loadGridData();
            return;
        }

        this.setModelMode();
        this.loadGridData();
    }

    public showLowestWccrGridOverlay() {
        if (this.lowestWccrGridApi) {
            this.lowestWccrGridApi.showLoadingOverlay();
        }
    }

    public hideLowestWccrGridOverlay() {
        if (this.lowestWccrGridApi) {
            this.lowestWccrGridApi.hideOverlay();
        }
    }

    public showLegalEntityCounterpartySummaryGridOverlay() {
        if (this.legalEntityCounterpartySummaryGridApi) {
            this.legalEntityCounterpartySummaryGridApi.showLoadingOverlay();
        }
    }

    public hideLegalEntityCounterpartySummaryGridOverlay() {
        if (this.legalEntityCounterpartySummaryGridApi) {
            this.legalEntityCounterpartySummaryGridApi.hideOverlay();
        }
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
